import React from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useTrial, TrialProvider } from './context/TrialContext';
import gastifyIconUrl from './assets/gastify-icon.svg';

import Login from './pages/Login';
import LoginReset from './pages/LoginReset';
import Registro from './pages/RegistroOnboarding';
import RegistroPlan from './pages/RegistroOnboardingPlan';
import Landing from './pages/Landing';
import Demo from './pages/Demo';
import PedirDemo from './pages/PedirDemo';
import { hasPublishedDemoVideos } from './data/publicSite';
import Funcionalidades from './pages/Funcionalidades';
import Modulo from './pages/Modulo';
import Precios from './pages/Precios';
// Pages removed or hidden (redirected to /)
// import Nosotros from './pages/Nosotros';
// import Soluciones from './pages/Soluciones';
import TerminosServicio from './pages/TerminosServicio';
import PoliticaPrivacidad from './pages/PoliticaPrivacidad';
import AcademiaImpulza from './pages/AcademiaImpulza';
import AcademiaViewer from './pages/academia/AcademiaViewer';
import Sistema from './pages/Sistema';
import Onboarding from './pages/Onboarding';
import Tienda from './pages/Tienda';
import Rastreo from './pages/Rastreo';

// Gastify billing pages
import PagoRequerido from './pages/PagoRequerido';
import GestionPlan from './pages/GestionPlan';
import SuscripcionStatus from './pages/SuscripcionStatus';
import HistorialPagos from './pages/HistorialPagos';

import './index.css';

// -------------------------------------------------------
// Guards
// -------------------------------------------------------
const ProtectedRoute = ({ children, requireOrg = true }) => {
  const { user, profile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--gst-cream-50)' }}>
        <div className="flex flex-col items-center gap-3">
          <img src={gastifyIconUrl} alt="Gastify" className="h-12 w-12 shrink-0 shadow-md" />
          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2" style={{ borderColor: 'var(--gst-mint-500)' }} />
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Guard de "limbo sin organización": si el usuario hizo signUp pero nunca
  // completó el paso 2 (elegir plan + crear org), lo mandamos directo ahí
  // para que no quede atrapado en /sistema/status sin poder hacer nada.
  if (requireOrg && !profile?.organization_id) {
    const isAlreadyOnPlanStep = location.pathname.startsWith('/registro-onboarding');
    if (!isAlreadyOnPlanStep) {
      return <Navigate to="/registro-onboarding/plan" replace />;
    }
  }

  return children;
};

/**
 * TrialGuard — wraps Sistema routes.
 *
 * - If BLOCKED (hard block by superadmin) → redirect to /sistema/pago-requerido
 * - If EXPIRED/READ-ONLY → allow access (user sees data, ReadOnlyBanner prevents operations)
 * - If GRACE → allow access (user sees warnings via ReadOnlyBanner)
 * - If TRIAL/ACTIVE → full access
 */
const TrialGuard = ({ children }) => {
  const { isBlocked, loadingTrial } = useTrial();

  if (loadingTrial) return null;

  // Only hard-blocked orgs get redirected. Expired orgs enter read-only mode.
  if (isBlocked) return <Navigate to="/sistema/pago-requerido" replace />;

  return children;
};

/**
 * StatusGuard — solo intercepta cuando hay algo que el usuario necesita ver.
 *
 * Antes: forzaba pantalla intersticial de 3s en CADA sesión (ruidoso).
 * Ahora: solo muestra status si hay advertencia o acción pendiente:
 *   - trial por vencer (showWarning)
 *   - período de gracia
 *   - vencido / read-only
 *   - bloqueado
 *   - pago en proceso (isPending)
 *
 * Si la suscripción está sana y con holgura, vamos derecho al dashboard.
 * El usuario mantiene siempre acceso al chip "Pagar mi plan" en la topbar.
 */
const StatusGuard = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    loadingTrial,
    showWarning,
    isGrace,
    isExpired,
    isReadOnly,
    isBlocked,
    isPending,
  } = useTrial();

  const needsStatusInterstitial =
    showWarning || isGrace || isExpired || isReadOnly || isBlocked || isPending;

  React.useEffect(() => {
    if (loadingTrial) return;
    if (sessionStorage.getItem('hasSeenStatus')) return;
    if (needsStatusInterstitial) {
      navigate('/sistema/status', { replace: true });
    } else {
      // Sin advertencias → marcamos como vista y dejamos entrar.
      sessionStorage.setItem('hasSeenStatus', 'true');
    }
  }, [loadingTrial, needsStatusInterstitial, location.pathname, navigate]);

  if (loadingTrial) return null;
  if (needsStatusInterstitial && !sessionStorage.getItem('hasSeenStatus')) return null;
  return children;
};

/**
 * RutaPublica
 * -----------
 * Pantallas del sitio publico que NO tienen sentido con la sesion abierta.
 * /login NO usa este guard: tiene su propia redireccion, porque el flujo de
 * contraseña provisoria (requires_pw_update) deja al usuario autenticado y
 * parado en /login a proposito.
 *
 * Por que existe: la ruta "/" renderizaba <Landing /> sin mirar la sesion, y
 * /login mostraba el formulario aunque ya estuvieras adentro. Entrar a
 * gastify.com.ar o recargar te dejaba en la landing publica con el boton
 * "Iniciar sesion" a la vista: la sesion de Supabase seguia viva, pero la
 * lectura del usuario era "me deslogueo" y volvia a loguearse de gusto.
 *
 * Mientras `loading` es true no decidimos nada: cortar antes de que
 * AuthContext resuelva getSession() seria reintroducir el mismo bug, esta vez
 * de verdad.
 */
const RutaPublica = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/sistema" replace />;
  return children;
};

// -------------------------------------------------------
// Inner app — wrapped by TrialProvider (needs auth)
// -------------------------------------------------------
function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (location.pathname === '/' && params.get('subscription_return') === '1') {
      navigate(`/sistema/status${location.search}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  return (
    <Routes>
      {/* ── Public ── */}
      <Route path="/login" element={<Login />} />
      <Route path="/login/reset" element={<LoginReset />} />
      <Route path="/registro-onboarding" element={<Registro />} />
      <Route path="/registro-onboarding/plan" element={<RegistroPlan />} />
      {/* La página de demo en video se publica sola cuando exista al menos un
          video cargado en src/data/publicSite.js; mientras tanto redirige. */}
      <Route path="/demo" element={hasPublishedDemoVideos() ? <Demo /> : <Navigate to="/pedir-demo" replace />} />
      <Route path="/pedir-demo" element={<PedirDemo />} />
      <Route path="/funcionalidades" element={<Funcionalidades />} />
      <Route path="/funcionalidades/:slug" element={<Modulo />} />
      <Route path="/precios" element={<Precios />} />
      <Route path="/nosotros" element={<Navigate to="/" replace />} />
      <Route path="/soluciones" element={<Navigate to="/" replace />} />
      <Route path="/soluciones/kioscos" element={<Navigate to="/" replace />} />
      <Route path="/soluciones/indumentaria" element={<Navigate to="/" replace />} />
      <Route path="/soluciones/gastronomia" element={<Navigate to="/" replace />} />
      <Route path="/legales/terminos" element={<TerminosServicio />} />
      <Route path="/legales/privacidad" element={<PoliticaPrivacidad />} />
      <Route path="/tienda/:slug" element={<Tienda />} />
      <Route path="/rastreo/:trackingCode" element={<Rastreo />} />

      {/* ── Billing pages (protected but NOT trial-gated) ── */}
      <Route
        path="/sistema/pago-requerido"
        element={
          <ProtectedRoute>
            <PagoRequerido />
          </ProtectedRoute>
        }
      />
      {/* /sistema/subir-comprobante — DEPRECATED: el cobro es 100% MP / AstroPay.
          Si alguien llega por bookmark viejo, lo redirige a Gestión de Plan. */}
      <Route
        path="/sistema/subir-comprobante"
        element={<Navigate to="/sistema/gestion-plan" replace />}
      />
      <Route
        path="/sistema/gestion-plan"
        element={
          <ProtectedRoute>
            <GestionPlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sistema/historial-pagos"
        element={
          <ProtectedRoute>
            <HistorialPagos />
          </ProtectedRoute>
        }
      />

      {/* ── Onboarding (protected, not trial-gated) ── */}
      <Route
        path="/sistema/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />

      {/* ── Status Interstitial ── */}
      <Route
        path="/sistema/status"
        element={
          <ProtectedRoute>
            <TrialGuard>
              <SuscripcionStatus />
            </TrialGuard>
          </ProtectedRoute>
        }
      />

      {/* ── ERP (protected + trial-gated) ── */}
      <Route
        path="/sistema/*"
        element={
          <ProtectedRoute>
            <TrialGuard>
              <StatusGuard>
                <Sistema />
              </StatusGuard>
            </TrialGuard>
          </ProtectedRoute>
        }
      />

      {/* ── Default ── */}
      <Route path="/" element={<RutaPublica><Landing /></RutaPublica>} />
      <Route path="*" element={<Navigate to="/sistema" replace />} />
    </Routes>
  );
}

// -------------------------------------------------------
// Root — TrialProvider lives here (outside Routes so it
// persists across navigations within the protected area)
// -------------------------------------------------------
export default function App() {
  return (
    <div className="min-h-screen" style={{ background: 'var(--gst-cream-50)' }}>
      <TrialProvider>
        <AppRoutes />
      </TrialProvider>
    </div>
  );
}
